import { useState, useEffect } from "react";
import API from "@/lib/api";
import { getErrorMessage } from "@/lib/errorHandler";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Users, Lock, Unlock, Key, CheckSquare, Square, AlertCircle, Trash2, Building2, MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";

export default function UserManagement() {
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const { selectedCampus, campuses, setSelectedCampus } = useCampusContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total_users: 0,
    active_users: 0,
    locked_users: 0
  });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lockReason, setLockReason] = useState("");
  const [bulkAction, setBulkAction] = useState("");

  useEffect(() => {
    loadUsers();
    loadStats();
  }, [page, search, statusFilter, selectedOrg, selectedCampus?.id]); // Reload when org/campus changes

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, role: "participant" }; // Takers only (converged role, migration c1r2s3t4u5v6)
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      // For superadmins: filter by organization when one is selected
      // Shows quiz takers who took quizzes from the selected organization
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
        if (selectedCampus?.id) params.campus_id = selectedCampus.id;
      }

      const res = await API.get("/user-management/users", { params });
      setUsers(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      toast.error("Failed to load users");
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const params = {};

      // For superadmins: filter stats by organization when one is selected
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
        if (selectedCampus?.id) params.campus_id = selectedCampus.id;
      }

      const res = await API.get("/user-management/user-stats", { params });
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  const handleLockUser = async () => {
    if (!selectedUser) return;

    if (!lockReason.trim()) {
      toast.error("Please provide a reason for locking the account");
      return;
    }

    try {
      await API.post(`/user-management/users/${selectedUser.id}/lock`, {
        reason: lockReason
      });
      toast.success("User account locked successfully");
      setShowLockDialog(false);
      setSelectedUser(null);
      setLockReason("");
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to lock user"));
    }
  };

  const handleUnlockUser = async () => {
    if (!selectedUser) return;

    try {
      await API.post(`/user-management/users/${selectedUser.id}/unlock`);
      toast.success("User account unlocked successfully");
      setShowUnlockDialog(false);
      setSelectedUser(null);
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to unlock user"));
    }
  };

  const handleResetOTP = async (user) => {
    if (!window.confirm(`Reset OTP attempts for "${user.name}"?`)) return;

    try {
      await API.post(`/user-management/users/${user.id}/reset-otp`);
      toast.success("OTP attempts reset successfully");
      loadUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to reset OTP"));
    }
  };

  const handleDeleteUser = async (user) => {
    const isAdmin = user.role === 'admin' || user.role === 'superadmin';
    const confirmMessage = isAdmin
      ? `Delete ${user.role} "${user.name}"?\n\nThis will permanently delete:\n- All their created quizzes, categories, and groups\n- All submissions to their quizzes\n\nThis action cannot be undone.`
      : `Delete user "${user.name}"?\n\nThis will permanently delete:\n- All their quiz submissions\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await API.delete(`/user-management/users/${user.id}`);
      toast.success("User deleted successfully");
      loadUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete user"));
    }
  };

  const handleBulkAction = async () => {
    if (selectedUsers.size === 0) {
      toast.error("Please select at least one user");
      return;
    }

    if (bulkAction === "lock" && !lockReason.trim()) {
      toast.error("Please provide a reason for locking accounts");
      return;
    }

    try {
      await API.post("/user-management/users/bulk-action", {
        user_ids: Array.from(selectedUsers),
        action: bulkAction,
        reason: bulkAction === "lock" ? lockReason : undefined
      });
      toast.success(`Successfully ${bulkAction === "lock" ? "locked" : "unlocked"} ${selectedUsers.size} users`);
      setShowBulkDialog(false);
      setSelectedUsers(new Set());
      setLockReason("");
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to perform bulk action"));
    }
  };

  const toggleUserSelection = (userId) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
  };

  const openLockDialog = (user) => {
    setSelectedUser(user);
    setLockReason("");
    setShowLockDialog(true);
  };

  const openUnlockDialog = (user) => {
    setSelectedUser(user);
    setShowUnlockDialog(true);
  };

  const openBulkDialog = (action) => {
    setBulkAction(action);
    setLockReason("");
    setShowBulkDialog(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="w-8 h-8" />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage regular user accounts (quiz takers)
            {isSuperadmin && selectedOrg && (
              <span className="ml-2 text-sm text-blue-600 font-medium">
                • Filtering by: {selectedOrg.name}{selectedCampus ? ` · ${selectedCampus.name}` : ''}
              </span>
            )}
            {isSuperadmin && !selectedOrg && (
              <span className="ml-2 text-sm text-gray-500">
                • Showing all quiz takers platform-wide
              </span>
            )}
          </p>
        </div>
        {selectedUsers.size > 0 && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openBulkDialog("lock")}>
              <Lock className="w-4 h-4 mr-2" />
              Lock Selected ({selectedUsers.size})
            </Button>
            <Button variant="outline" onClick={() => openBulkDialog("unlock")}>
              <Unlock className="w-4 h-4 mr-2" />
              Unlock Selected ({selectedUsers.size})
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_users}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Active Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.active_users}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Locked Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats.locked_users}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="locked">Locked</option>
            </select>
            {isSuperadmin && selectedOrg && campuses.length > 1 && (
              <Select value={selectedCampus?.id || "all"} onValueChange={(val) => {
                setSelectedCampus(val === "all" ? null : campuses.find(c => c.id === val));
                setPage(1);
              }}>
                <SelectTrigger className="w-44 h-10 border-slate-200 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400 mr-1" />
                  <SelectValue placeholder="All Campuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campuses</SelectItem>
                  {campuses.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setSearch("");
                setStatusFilter("");
              }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium w-12">
                    <Checkbox
                      checked={selectedUsers.size === users.length && users.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="text-left p-4 font-medium">Name</th>
                  <th className="text-left p-4 font-medium">Email</th>
                  <th className="text-left p-4 font-medium">Role</th>
                  <th className="text-left p-4 font-medium">Organization</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Last Assessment</th>
                  <th className="text-left p-4 font-medium">Created</th>
                  <th className="text-left p-4 font-medium">Last Login</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <>
                    <style>{`@keyframes um-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
                    {[...Array(7)].map((_, i) => (
                      <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td className="p-4 w-12"><div style={{width:16,height:16,borderRadius:4,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:120,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:160,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:20,width:60,borderRadius:10,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:100,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:20,width:70,borderRadius:10,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:80,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4 text-right"><div style={{height:28,width:60,borderRadius:8,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'um-shimmer 1.4s ease infinite',marginLeft:'auto'}} /></td>
                      </tr>
                    ))}
                  </>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center">
                      <svg width="72" height="64" viewBox="0 0 72 64" fill="none" className="mx-auto mb-4 opacity-70">
                        <circle cx="36" cy="22" r="12" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                        <circle cx="36" cy="22" r="6" fill="#A5B4FC"/>
                        <path d="M14 54c2-10 11-17 22-17s20 7 22 17" stroke="#C7D2FE" strokeWidth="2" strokeLinecap="round" fill="none"/>
                      </svg>
                      <p className="text-slate-600 font-semibold text-sm">No users found</p>
                      <p className="text-slate-400 text-xs mt-1">Try a different search or filter</p>
                    </td>
                  </tr>
                ) : users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <Checkbox
                          checked={selectedUsers.has(user.id)}
                          onCheckedChange={() => toggleUserSelection(user.id)}
                          disabled={user.role === 'superadmin'}
                        />
                      </td>
                      <td className="p-4">{user.name}</td>
                      <td className="p-4">{user.email}</td>
                      <td className="p-4">
                        <Badge
                          variant={
                            user.role === 'superadmin' ? 'default' :
                            user.role === 'admin' ? 'secondary' :
                            'outline'
                          }
                        >
                          {user.role}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {user.organization ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium">{user.organization.name}</div>
                              {user.organization.plan && (
                                <div className="text-xs text-gray-500">{user.organization.plan}</div>
                              )}
                              {user.organization.from_quizzes && (
                                <div className="text-xs text-gray-500 italic">from quiz submissions</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.locked_until ? (
                          <Badge variant="destructive">
                            <Lock className="w-3 h-3 mr-1" />
                            Locked
                          </Badge>
                        ) : user.failed_otp_attempts > 0 ? (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {user.failed_otp_attempts} failed attempts
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Active
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        {user.last_assessment ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 truncate max-w-[200px]" title={user.last_assessment.quiz_title}>
                              {user.last_assessment.quiz_title}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(user.last_assessment.completed_at)}
                              {user.last_assessment.percentage !== undefined && (
                                <span className="ml-2 text-gray-600">• {user.last_assessment.percentage}%</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No assessments</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {formatDate(user.last_login_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {user.role !== 'superadmin' && (
                            <>
                              {user.locked_until ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openUnlockDialog(user)}
                                  title="Unlock Account"
                                >
                                  <Unlock className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openLockDialog(user)}
                                  title="Lock Account"
                                >
                                  <Lock className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResetOTP(user)}
                                title="Reset OTP"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(user)}
                                title="Delete User"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2">Page {page} of {Math.ceil(total / 20)}</span>
          <Button
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 20)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Lock User Dialog */}
      <Dialog open={showLockDialog} onOpenChange={setShowLockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              Lock User Account
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  You are about to lock the account for <strong>{selectedUser.name}</strong> ({selectedUser.email}).
                  They will not be able to log in until unlocked.
                </p>
              </div>

              <div>
                <Label>Reason for Locking *</Label>
                <Textarea
                  value={lockReason}
                  onChange={(e) => setLockReason(e.target.value)}
                  placeholder="Enter the reason for locking this account..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLockDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLockUser}>
              Lock Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock User Dialog */}
      <Dialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-green-600" />
              Unlock User Account
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  You are about to unlock the account for <strong>{selectedUser.name}</strong> ({selectedUser.email}).
                  They will be able to log in immediately.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlockDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUnlockUser}>
              Unlock Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === "lock" ? (
                <>
                  <Lock className="w-5 h-5 text-red-600" />
                  Bulk Lock Accounts
                </>
              ) : (
                <>
                  <Unlock className="w-5 h-5 text-green-600" />
                  Bulk Unlock Accounts
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className={`border rounded-lg p-4 ${bulkAction === "lock" ? "bg-yellow-50 border-yellow-200" : "bg-green-50 border-green-200"}`}>
              <p className={`text-sm ${bulkAction === "lock" ? "text-yellow-800" : "text-green-800"}`}>
                You are about to {bulkAction} <strong>{selectedUsers.size}</strong> user account(s).
              </p>
            </div>

            {bulkAction === "lock" && (
              <div>
                <Label>Reason for Locking *</Label>
                <Textarea
                  value={lockReason}
                  onChange={(e) => setLockReason(e.target.value)}
                  placeholder="Enter the reason for locking these accounts..."
                  rows={3}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
              Cancel
            </Button>
            <Button
              variant={bulkAction === "lock" ? "destructive" : "default"}
              onClick={handleBulkAction}
            >
              {bulkAction === "lock" ? "Lock Accounts" : "Unlock Accounts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
